// The Ministry Model — a greybox 3D diorama (docs/04 §3, docs/05). Pure presentation: it never
// reads game state on its own; the Director and the Game screen drive it.
import * as THREE from 'three';
import CameraControls from 'camera-controls';
import { gsap } from 'gsap';
import type { LocationId, PlayerView } from '@bcm/rules';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { ANNEX_BAYS, BENCH_FLANK, LOBBY_X, LOBBY_Z, PEW_TOP, PEW_WIDTH, ROOMS, ROOM_BY_ID, benchSide, benchSlot, type RoomLayout } from './layout';
import { AnnexBay, AnnexRoom } from './annex';
import { BoardDecor } from './decor';
import { disposeFx3d, dust, floorRing, glint, motes } from './fx3d';
import { LABEL_LAYER, plaqueSprite } from './labels';
import { BASE, ROOM_FLOOR_Y, buildMinistry, type MinistryBuild } from './ministry';
import { loadProps, props, type PropLibrary } from './assets';
import { PostFX, type Quality } from './post';
import { carpet, disposeTextures, radial } from './textures';
import { toonFrom } from './materials';
import { TableCards, type CardPick } from './TableCards';
import { disposeCardFaces } from './cardFaces';
import { AMBIENT, boardObscured } from './pacing';
import { C, EMBLEMS, SEAL_COLORS, SEAL_TRIMS, shortLocation } from '../game/content';
import { LOCATION_ICON, drawIcon } from '../game/icons';
import { devicePixels } from '../app/pixels';

CameraControls.install({ THREE });

const wait = (s: number) => new Promise<void>((r) => setTimeout(r, s * 1000));

export type Highlight = 'legal' | 'target' | 'audit' | 'dim' | null;

interface RoomView {
  layout: RoomLayout;
  floor: THREE.Mesh;
  floorMat: THREE.MeshStandardMaterial;
  baseFloor: THREE.Color;
  rim: THREE.Group;
  rimMat: THREE.MeshBasicMaterial;
  glow: THREE.Mesh;
  glowMat: THREE.MeshBasicMaterial;
  closedSign: THREE.Object3D;
  tape: THREE.Object3D;
  highlight: Highlight;
  pulse: gsap.core.Tween | null;
  light?: THREE.PointLight;
}

interface AssistantView {
  obj: THREE.Group;
  inner: THREE.Group;
  seat: number;
  busy: boolean;
  /** Resting scale: the Afternoon Shift's Interns are drawn smaller. */
  base: number;
  /** An Afternoon Shift Intern: a paper hat instead of the wizard's hat and emblem. */
  intern: boolean;
  /** Where it is scooting along its pew to, while it re-centres after a neighbour left. */
  scootX?: number;
}

const CLERK_SCALE = 1.35;
/** The Stamp of Precedence on the benches: large enough to read from the default camera. */
const STAMP_SCALE = 1.3;
const INTERN_SCALE = 1.05;

const FLOOR_Y = ROOM_FLOOR_Y + 0.02;
const DEFAULT_POLAR = THREE.MathUtils.degToRad(27);
const DEFAULT_DISTANCE = 52;

export class BoardScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: CameraControls;
  private timer = new THREE.Timer();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private rooms = new Map<LocationId, RoomView>();
  /** Every room's layout: the Ministry's fixed rooms, plus the Annex rooms while they stand (P-18). */
  private layouts = new Map<LocationId, RoomLayout>(ROOM_BY_ID);
  /** The two Annex bays at the front corners of the model (shown when the rules have them). */
  private bays: AnnexBay[] = [];
  private annexOn = false;
  /** Board marks for the rules in force: Resolutions, toll barriers, Interns at the door. */
  readonly decor: BoardDecor;
  /** Something is being built: re-render the (normally static) shadow map every frame. */
  private shadowLive = 0;
  private assistants = new Map<string, AssistantView>();
  private benches: THREE.Group[] = [];
  /** The Stamp of Precedence (P-11): stands by the bench of whoever holds it. */
  private stamp = (() => {
    const s = makeStamp();
    s.scale.setScalar(STAMP_SCALE);
    return s;
  })();
  private stampBusy = false;
  private hemi: THREE.HemisphereLight;
  private sun: THREE.DirectionalLight;
  private ministry: MinistryBuild;
  private post: PostFX | null = null;
  private envTarget: THREE.WebGLRenderTarget;
  private raf = 0;
  private hovered: LocationId | null = null;
  private down: { x: number; y: number } | null = null;
  /** Touch: fingers currently on the board, and what the last tap selected (tap again to act). */
  private touches = new Set<number>();
  private tapped: string | null = null;
  private keys = new Set<string>();
  private resizeObs: ResizeObserver;
  private lastUserInput = 0;
  reducedMotion = false;

  readonly cards: TableCards;
  private framedExtent = '';
  onCardClick: ((pick: CardPick) => void) | null = null;
  onCardHover: ((pick: CardPick | null, x: number, y: number) => void) | null = null;
  onRoomClick: ((loc: LocationId) => void) | null = null;
  onRoomHover: ((loc: LocationId | null, x: number, y: number) => void) | null = null;

  constructor(
    private container: HTMLElement,
    quality: Quality = 'high',
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: quality === 'low', powerPreference: 'high-performance' });
    // The canvas always matches the screen's pixel ratio (capped at 2), so nothing is upscaled by
    // the browser. Slow GPUs trade internal scene resolution instead (see adaptResolution).
    this.renderer.setPixelRatio(Math.min(quality === 'low' ? 1.25 : 2, devicePixels()));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // The Ministry never moves: render its shadow map once (and when the sun moves). Pieces use
    // their own soft blob shadows, so they never need the shadow pass.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.domElement.className = 'board-canvas';
    container.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color('#120c09');
    this.scene.fog = new THREE.Fog('#120c09', 90, 170);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.envTarget = pmrem.fromScene(new RoomEnvironment(), 0.04);
    this.scene.environment = this.envTarget.texture;
    this.scene.environmentIntensity = 0.3;
    pmrem.dispose();

    this.camera = new THREE.PerspectiveCamera(32, 1, 2, 260);
    this.controls = new CameraControls(this.camera, this.renderer.domElement);
    this.configureControls();

    this.hemi = new THREE.HemisphereLight('#fff1dc', '#3a2616', 0.52);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight('#ffe2b8', 2.2);
    this.sun.position.set(-18, 38, 24);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(quality === 'low' ? 1024 : 2048, quality === 'low' ? 1024 : 2048);
    const sc = this.sun.shadow.camera;
    sc.left = -34;
    sc.right = 34;
    sc.top = 24;
    sc.bottom = -24;
    sc.near = 5;
    sc.far = 110;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.03;
    this.sun.shadow.radius = 3;
    this.scene.add(this.sun);

    this.ministry = buildMinistry(this.scene);
    for (const r of ROOMS) this.buildRoom(r);
    this.buildLobby();
    this.bays = ANNEX_BAYS.map((_, i) => new AnnexBay(i, this.scene));
    this.decor = new BoardDecor(this.scene);
    this.stamp.visible = false;
    this.scene.add(this.stamp);
    this.cards = new TableCards(this.scene);
    // The label pass (see loop) shares this scene's render state. If it saw a different set of
    // lights, three.js would re-validate the program of every lit material on every frame — that
    // doubled the CPU cost of a frame — so the lights are on the label layer too.
    this.scene.traverse((o) => void ((o as THREE.Light).isLight && o.layers.enable(LABEL_LAYER)));

    const el = this.renderer.domElement;
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerCancel);
    el.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch') return; // a lifted finger "leaves"; the tap selection stays
      this.setHover(null, 0, 0);
      this.cards.hover(null);
      this.onCardHover?.(null, 0, 0);
    });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    el.addEventListener('wheel', () => (this.lastUserInput = performance.now()), { passive: true });
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'wheel'] as const) el.addEventListener(type, this.wake, { passive: true });
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
    this.resizeObs = new ResizeObserver(() => {
      this.resize();
      this.wake();
      if (this.container.clientWidth > 0 && this.lastUserInput === 0) this.resetCamera(false);
    });
    this.resizeObs.observe(container);
    this.post = new PostFX(this.renderer, this.scene, this.camera, quality);
    this.resize();
    // Reveal only once the props are in and every shader is compiled (off the main thread where
    // KHR_parallel_shader_compile exists), so the first frame never hitches.
    this.renderer.domElement.style.opacity = '0';
    this.ready = loadProps()
      .then((lib) => {
        this.onProps(lib);
        return this.renderer.compileAsync(this.scene, this.camera).catch(() => undefined);
      })
      .catch(() => undefined)
      .then(() => {
        if (this.disposed) return;
        this.raf = requestAnimationFrame(this.loop);
        const el = this.renderer.domElement;
        el.style.transition = 'opacity 0.6s ease';
        requestAnimationFrame(() => (el.style.opacity = '1'));
      });
  }

  /** Resolves when the Ministry is furnished, compiled and on screen. */
  readonly ready: Promise<void>;

  /** The prop library arrived: furnish the Ministry and swap placeholder pieces for real ones. */
  private onProps(lib: PropLibrary): void {
    if (this.disposed) return;
    this.ministry.furnish(lib);
    this.ministry.shadeGround(this.renderer);
    this.renderer.shadowMap.needsUpdate = true;
    for (const av of this.assistants.values()) this.dressAssistant(av, lib);
    for (const bay of this.bays) bay.room?.furnish(lib);
    this.benches.forEach((b, seat) => {
      const old = b.getObjectByName('bench');
      if (old) b.remove(old);
      const pew = lib.make('bench');
      pew.name = 'bench';
      pew.traverse((o) => void (o.castShadow = false));
      b.add(pew);
      const cushion = b.getObjectByName('band');
      if (cushion) cushion.position.y = PEW_TOP - 0.04;
      void seat;
    });
  }

  // ─── Construction ──────────────────────────────────────────────────────────

  private configureControls(): void {
    const c = this.controls;
    const A = CameraControls.ACTION;
    c.mouseButtons.left = A.NONE;
    c.mouseButtons.right = A.TRUCK;
    c.mouseButtons.middle = A.ROTATE;
    c.mouseButtons.wheel = A.DOLLY;
    c.touches.one = A.TOUCH_TRUCK;
    c.touches.two = A.TOUCH_DOLLY_TRUCK;
    c.dollyToCursor = true;
    c.minPolarAngle = THREE.MathUtils.degToRad(12);
    c.maxPolarAngle = THREE.MathUtils.degToRad(60);
    c.minAzimuthAngle = THREE.MathUtils.degToRad(-25);
    c.maxAzimuthAngle = THREE.MathUtils.degToRad(25);
    c.minDistance = 14;
    c.maxDistance = 130;
    c.smoothTime = 0.18;
    c.draggingSmoothTime = 0.08;
    c.setBoundary(new THREE.Box3(new THREE.Vector3(-40, -1, -16), new THREE.Vector3(44, 2, 21)));
    c.boundaryEnclosesCamera = false;
    c.addEventListener('controlstart', () => (this.lastUserInput = performance.now()));
    c.addEventListener('transitionstart', this.wake);
    c.addEventListener('control', this.wake);
    this.resetCamera(false);
  }

  private disposed = false;

  /** Screen-space insets (px) covered by floating HUD panels; framing keeps the table clear of them. */
  private safe = { left: 0, right: 0, top: 0, bottom: 0 };

  setSafeArea(insets: { left: number; right: number; top: number; bottom: number }): void {
    const changed = (Object.keys(insets) as (keyof typeof insets)[]).some((k) => Math.abs(insets[k] - this.safe[k]) > 2);
    this.safe = { ...insets };
    if (changed && this.lastUserInput === 0) this.resetCamera(false);
  }

  resetCamera(animate = true): void {
    // An explicit reset hands framing back to the game until the player moves the camera again.
    if (animate) this.lastUserInput = 0;
    this.focused = null;
    // Fit the Ministry and the cards on the table beside it into the unobstructed part of the view.
    const areas: [number, number, number, number, number][] = [[BASE.x[0], BASE.x[1], BASE.z[0] + 1.2, BASE.z[1], 3.4]];
    for (const [a, b, c, d] of this.cards?.footprints() ?? []) areas.push([a, b, c, d, 0.6]);
    // The Annex wings (P-18) sit in table space the cards already span; framing them costs nothing.
    if (this.annexOn) for (const b of ANNEX_BAYS) areas.push([b.x[0], b.x[1], b.z[0], b.z[1], 2.4]);
    this.fitAreas(areas, DEFAULT_POLAR, this.safe, animate);
  }

  /**
   * Exact projection fit (camera-controls' fitToBox is conservative for tilted views): place the
   * camera at `polar` so every area's corners land inside the screen minus the given insets.
   */
  private fitAreas(areas: [number, number, number, number, number][], polar: number, inset: { left: number; right: number; top: number; bottom: number }, animate: boolean): void {
    const corners: THREE.Vector3[] = [];
    for (const [ax0, ax1, az0, az1, h] of areas) {
      for (const x of [ax0, ax1]) for (const y of [-0.6, h]) for (const z of [az0, az1]) corners.push(new THREE.Vector3(x, y, z));
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    const W = rect.width || this.container.clientWidth || 1;
    const H = rect.height || this.container.clientHeight || 1;
    const margin = 14;
    const nx0 = -1 + (2 * (inset.left + margin)) / W;
    const nx1 = 1 - (2 * (inset.right + margin)) / W;
    const ny0 = -1 + (2 * (inset.bottom + margin)) / H;
    const ny1 = 1 - (2 * (inset.top + margin)) / H;
    const cam = this.camera.clone();
    cam.aspect = W / H;
    cam.updateProjectionMatrix();
    const dir = new THREE.Vector3(0, Math.cos(polar), Math.sin(polar));
    const cx = corners.reduce((n, c) => n + c.x, 0) / corners.length;
    const cz = corners.reduce((n, c) => n + c.z, 0) / corners.length;
    const target = new THREE.Vector3(cx, 0, cz);
    let dist = DEFAULT_DISTANCE;
    const right = new THREE.Vector3(1, 0, 0);
    const up = new THREE.Vector3().crossVectors(dir, right).normalize();
    for (let i = 0; i < 14; i++) {
      cam.position.copy(target).addScaledVector(dir, dist);
      cam.lookAt(target);
      cam.updateMatrixWorld();
      let [minX, maxX, minY, maxY] = [Infinity, -Infinity, Infinity, -Infinity];
      for (const c of corners) {
        const p = c.clone().project(cam);
        minX = Math.min(minX, p.x);
        maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y);
        maxY = Math.max(maxY, p.y);
      }
      const scale = Math.max((maxX - minX) / (nx1 - nx0), (maxY - minY) / (ny1 - ny0));
      // Re-centre: shift the target by the NDC offset converted to world units at this distance.
      const halfH = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)) * dist;
      const halfW = halfH * cam.aspect;
      const dx = ((minX + maxX) / 2 - (nx0 + nx1) / 2) * halfW;
      const dy = ((minY + maxY) / 2 - (ny0 + ny1) / 2) * halfH;
      target.addScaledVector(right, dx).addScaledVector(up, dy);
      target.y = 0;
      dist = Math.max(this.controls.minDistance, dist * scale);
    }
    cam.position.copy(target).addScaledVector(dir, dist);
    void this.controls.setLookAt(cam.position.x, cam.position.y, cam.position.z, target.x, target.y, target.z, animate);
  }

  private mat(color: string, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) {
    return toonFrom({ color, ...extra });
  }

  private box(w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    this.scene.add(m);
    return m;
  }

  /**
   * A room's interactive layer: highlights, stations, the closed sign and the audit tape. The
   * Ministry's rooms use their floor from the static build; an Annex room passes its own floor and
   * where its pieces go (they come and go with the room).
   */
  private buildRoom(r: RoomLayout, annex?: AnnexRoom): void {
    const w = r.x[1] - r.x[0];
    const d = r.z[1] - r.z[0];
    const cx = (r.x[0] + r.x[1]) / 2;
    const cz = (r.z[0] + r.z[1]) / 2;
    const parent: THREE.Object3D = annex?.group ?? this.scene;

    const floor = annex?.floor ?? this.ministry.floors.get(r.id)!;
    const floorMat = floor.material as THREE.MeshStandardMaterial;

    const glowMat = new THREE.MeshBasicMaterial({ color: r.accent, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(w - 0.5, d - 0.5), glowMat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.set(cx, FLOOR_Y + 0.02, cz);
    glow.visible = false; // shown only while lit (applyGlow)
    parent.add(glow);

    // Rim: a solid painted frame just inside the walls for legal / target / audit / hover states —
    // clearly readable without glow (Mockup 1's lit room outlines, minus the bloom).
    const rimMat = new THREE.MeshBasicMaterial({ color: '#ffe9a8', transparent: true, opacity: 0, depthWrite: false });
    const rim = new THREE.Group();
    // A slim gilded line on the inner edge of the floor's border inlay: calm when many rooms are
    // lit at once, still unmistakable on hover.
    const band = 0.13;
    const inset = 0.6;
    for (const [bw, bd, bx, bz] of [
      [w - 2 * inset, band, cx, r.z[0] + inset + band / 2],
      [w - 2 * inset, band, cx, r.z[1] - inset - band / 2],
      [band, d - 2 * inset - 2 * band, r.x[0] + inset + band / 2, cz],
      [band, d - 2 * inset - 2 * band, r.x[1] - inset - band / 2, cz],
    ] as const) {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(bw, bd), rimMat);
      strip.rotation.x = -Math.PI / 2;
      strip.position.set(bx, FLOOR_Y + 0.04, bz);
      rim.add(strip);
    }
    rim.visible = false; // shown only while lit (applyGlow)
    parent.add(rim);

    const light = this.ministry.lights.get(r.id);

    // Stations (2 per room, docs/05 §3.2) and the "Window Closed" sign on B.
    const brass = this.mat('#c9a14e', { metalness: 0.9, roughness: 0.3 });
    const inlay = this.mat(r.tint, { roughness: 0.5 });
    // A room with a fixed capacity of 1 (the Office of Precedence) has a single station.
    const stations = C.locations.get(r.id)?.capacity === 1 ? r.stations.slice(0, 1) : r.stations;
    for (const [sx, sz] of stations) {
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.72, 0.05, 40), brass);
      disc.position.set(sx, ROOM_FLOOR_Y + 0.025, sz);
      disc.receiveShadow = true;
      const inner = new THREE.Mesh(new THREE.CylinderGeometry(0.56, 0.56, 0.052, 40), inlay);
      inner.position.copy(disc.position);
      inner.receiveShadow = true;
      if (annex) {
        // On the Annex floor, so the stations spread out with it as the room is built.
        const fg = annex.floorGroup;
        for (const m of [disc, inner]) m.position.sub(fg.position);
        fg.add(disc, inner);
      } else this.ministry.statics.add(disc, inner);
    }
    const [bx, bz] = r.stations[1];
    const closedSign = new THREE.Group();
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.1, 8), this.mat('#3a2a20'));
    post.position.y = 0.6;
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.42, 0.05), this.mat('#8e2a2a'));
    plate.position.y = 1.15;
    closedSign.add(post, plate);
    closedSign.position.set(bx, FLOOR_Y, bz);
    closedSign.visible = false;
    parent.add(closedSign);

    // Audit tape: two red bars across the room.
    const tape = new THREE.Group();
    const tapeMat = new THREE.MeshStandardMaterial({ color: '#c0262d', emissive: '#5a0c0f', roughness: 0.5 });
    const len = Math.hypot(w, d) * 0.7;
    for (const sign of [1, -1]) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(len, 0.08, 0.35), tapeMat);
      bar.rotation.y = sign * Math.atan2(d, w);
      bar.position.y = 1.2;
      tape.add(bar);
    }
    const auditLabel = plaqueSprite({ title: 'AUDITED', dark: true, accent: '#c0262d', width: 420 }, 3);
    auditLabel.position.y = 2.2;
    tape.add(auditLabel);
    tape.position.set(cx, 0, cz);
    tape.visible = false;
    parent.add(tape);

    this.rooms.set(r.id, {
      layout: r,
      floor,
      floorMat,
      baseFloor: floorMat.color.clone(),
      rim,
      rimMat,
      glow,
      glowMat,
      closedSign,
      tape,
      highlight: null,
      pulse: null,
      ...(light ? { light } : {}),
    });
  }

  private buildLobby(): void {
    for (let seat = 0; seat < 5; seat++) {
      const g = new THREE.Group();
      const color = SEAL_COLORS[seat]!;
      const bench = new THREE.Mesh(new THREE.BoxGeometry(PEW_WIDTH + 0.2, 0.35, 0.9), this.mat('#5c3f2c'));
      bench.name = 'bench';
      bench.position.y = 0.3;
      bench.castShadow = false;
      // The cushion the clerks stand on: in front of the backrest, its top at PEW_TOP.
      const band = new THREE.Mesh(new THREE.BoxGeometry(PEW_WIDTH - 0.2, 0.08, 0.58), this.mat(color, { roughness: 0.7 }));
      band.name = 'band';
      band.position.set(0, 0.5, 0.04);
      g.add(bench, band, seatStandard(color, benchSide(seat)), seatRug(color));
      const lib = props();
      if (lib) {
        g.remove(bench);
        const pew = lib.make('bench');
        pew.name = 'bench';
        pew.traverse((o) => void (o.castShadow = false));
        g.add(pew);
      }
      g.position.set(LOBBY_X[seat]!, 0, LOBBY_Z);
      g.visible = false;
      this.scene.add(g);
      this.benches.push(g);
    }
  }

  setSeats(names: string[]): void {
    this.benches.forEach((b, i) => {
      b.visible = i < names.length;
      const old = b.getObjectByName('label');
      if (old) b.remove(old);
      if (i < names.length) {
        const label = plaqueSprite({ title: names[i]!, dark: true, accent: SEAL_COLORS[i] }, 3.4);
        label.name = 'label';
        label.position.set(0, 1.25, 0.9);
        b.add(label);
      }
    });
  }

  // ─── Assistants ────────────────────────────────────────────────────────────

  private makeAssistant(seat: number, intern = false): AssistantView {
    const obj = new THREE.Group();
    const inner = new THREE.Group();
    const color = SEAL_COLORS[seat]!;
    // Seat-coloured base ring (always readable from above, even when the piece is occluded).
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.46, 0.6, 32), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, toneMapped: false }));
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02;
    const shadow = new THREE.Mesh(new THREE.CircleGeometry(0.55, 24), new THREE.MeshBasicMaterial({ map: radial(), transparent: true, depthWrite: false }));
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.012;
    obj.add(inner, shadow, ring);
    const base = intern ? INTERN_SCALE : CLERK_SCALE;
    obj.scale.setScalar(base);
    this.scene.add(obj);
    const av: AssistantView = { obj, inner, seat, busy: false, base, intern };
    const lib = props();
    if (lib) this.dressAssistant(av, lib);
    else {
      const robe = new THREE.Mesh(new THREE.ConeGeometry(0.4, 1.3, 18), this.mat(color, { roughness: 0.6 }));
      robe.position.y = 0.65;
      robe.castShadow = false;
      robe.name = 'placeholder';
      inner.add(robe);
    }
    if (!this.reducedMotion) {
      gsap.to(inner.position, { y: 0.06, duration: 0.9 + Math.random() * 0.5, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: Math.random(), data: AMBIENT });
    }
    return av;
  }

  private dressAssistant(av: AssistantView, lib: PropLibrary): void {
    const old = av.inner.getObjectByName('placeholder');
    if (old) av.inner.remove(old);
    if (av.inner.getObjectByName('meeple')) return;
    const tint = { accent: SEAL_COLORS[av.seat]!, trim: SEAL_TRIMS[av.seat]! };
    const m = lib.make(av.intern ? 'meeple_intern' : `meeple_${EMBLEMS[av.seat]!}`, tint);
    m.name = 'meeple';
    m.scale.setScalar(0.95);
    m.traverse((o) => void (o.castShadow = false));
    av.inner.add(m);
  }

  /** Show or hide an assistant's base ring and floor shadow (hidden on the pew and in the air). */
  private setGround(av: AssistantView, on: boolean): void {
    for (const ch of av.obj.children) if (ch !== av.inner) ch.visible = on;
  }

  /** Put a resting assistant at `to`. A clerk already on its pew scoots along it; anything else teleports. */
  private settle(av: AssistantView, to: THREE.Vector3): void {
    const p = av.obj.position;
    const onPew = isPewSpot(to);
    this.setGround(av, !onPew);
    const dx = to.x - p.x;
    if (onPew && isPewSpot(p) && Math.abs(dx) > 0.01 && Math.abs(dx) < PEW_WIDTH && !this.reducedMotion) {
      if (av.scootX === to.x) return;
      av.scootX = to.x;
      gsap.to(p, { x: to.x, duration: 0.35, ease: 'power2.inOut', overwrite: true, onComplete: () => void (av.scootX = undefined) });
      return;
    }
    if (av.scootX !== undefined) gsap.killTweensOf(p);
    av.scootX = undefined;
    p.copy(to);
  }

  private targetFor(view: PlayerView, seat: number, id: string): THREE.Vector3 {
    const st = view.seats[seat]!;
    const a = st.assistants.find((x) => x.id === id)!;
    // A clerk in an Annex room that has since closed (replaced at the end of the round) waits on
    // the pew until Upkeep.
    const home = (x: typeof a) => (!x.location && !x.placed) || (!!x.location && !this.layouts.has(x.location));
    if (a.location && this.layouts.has(a.location)) {
      const r = this.layouts.get(a.location)!;
      const idx = view.board[a.location].occupants.indexOf(id);
      const [sx, sz] = r.stations[Math.min(1, Math.max(0, idx))]!;
      const extra = Math.max(0, idx - 1) * 0.9; // Harlan: a third clerk squeezes in
      return new THREE.Vector3(sx + extra, FLOOR_Y + 0.08, sz);
    }
    // Sent-home clerks are hidden until Upkeep, so they don't hold a place on the pew.
    const waiting = st.assistants.filter(home);
    const slot = waiting.findIndex((x) => x.id === id);
    const [bx, bz] = benchSlot(seat, Math.max(0, slot), waiting.length);
    return new THREE.Vector3(bx, PEW_TOP, bz);
  }

  /** Teleport every assistant that is not animating to where the view says it is. */
  sync(view: PlayerView): void {
    const seen = new Set<string>();
    for (const st of view.seats) {
      for (const a of st.assistants) {
        seen.add(a.id);
        let av = this.assistants.get(a.id);
        if (!av) {
          av = this.makeAssistant(st.seat, !!a.intern);
          this.assistants.set(a.id, av);
          av.obj.position.copy(this.targetFor(view, st.seat, a.id));
        }
        if (!av.busy) this.settle(av, this.targetFor(view, st.seat, a.id));
        av.obj.visible = !(a.placed && !a.location); // sent home: hidden
      }
    }
    for (const [id, av] of this.assistants) {
      if (!seen.has(id)) {
        this.scene.remove(av.obj);
        gsap.killTweensOf(av.inner.position);
        this.assistants.delete(id);
      }
    }
    this.syncAnnex(view);
    this.decor.reducedMotion = this.reducedMotion;
    this.decor.sync(view, props());
    for (const [id, room] of this.rooms) this.setBlocked(id, (view.board[id]?.blockedBy.length ?? 0) > 0, room);
    const cap = view.capacity ?? view.options.capacity[String(view.playerCount)] ?? 2;
    // "Window closed" on the second station at 1-per-room player counts (not where one is the rule).
    for (const [id, room] of this.rooms) room.closedSign.visible = cap < 2 && !C.locations.get(id)?.capacity;
    if (!this.stampBusy) {
      this.stamp.visible = view.phase !== 'draft' && view.round > 0;
      this.stamp.position.copy(this.stampSpot(view.stampHolder ?? view.firstSeat));
    }
    this.cards.sync(view);
    // Re-frame once if the table grew (e.g. a 12-problem v1 layout), unless the player moved the camera.
    const extent = this.cards.extentX().join(',');
    if (extent !== this.framedExtent) {
      const first = this.framedExtent === '';
      this.framedExtent = extent;
      if (this.lastUserInput === 0) this.resetCamera(!first);
    }
  }

  cardScreenPos(pick: CardPick): { x: number; y: number } {
    return this.screenPos(this.cards.anchor(pick));
  }

  /** The "Ministry hop" (docs/05 §6): pick-up, arc over the walls, land, scurry to the station. */
  hop(id: string, seat: number, to: THREE.Vector3, speed = 1): Promise<void> {
    let av = this.assistants.get(id);
    if (!av) {
      av = this.makeAssistant(seat);
      this.assistants.set(id, av);
      const [bx, bz] = benchSlot(seat, 0, 1);
      av.obj.position.set(bx, PEW_TOP, bz);
    }
    const a = av;
    a.busy = true;
    gsap.killTweensOf(a.obj.position);
    a.scootX = undefined;
    this.setGround(a, false); // in the air: no base ring or floor shadow
    const from = a.obj.position.clone();
    const dist = from.distanceTo(to);
    const arc = 2 + 0.08 * dist;
    const dur = THREE.MathUtils.clamp(0.45 + dist * 0.012, 0.45, 0.85);
    const p = { t: 0 };
    const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
    tl.timeScale(this.reducedMotion ? speed * 2 : speed);
    const k = a.base / CLERK_SCALE;
    tl.to(a.obj.scale, { y: 1.05 * k, x: 1.5 * k, z: 1.5 * k, duration: 0.08, ease: 'power2.out' })
      .to(a.obj.scale, { y: 1.55 * k, x: 1.25 * k, z: 1.25 * k, duration: 0.08, ease: 'power2.out' })
      .to(p, {
        t: 1,
        duration: dur,
        ease: 'power1.inOut',
        onUpdate: () => {
          a.obj.position.lerpVectors(from, to, p.t);
          a.obj.position.y = THREE.MathUtils.lerp(from.y, to.y, p.t) + (this.reducedMotion ? 0 : arc * 4 * p.t * (1 - p.t));
        },
      })
      .to(a.obj.scale, { y: 1.1 * k, x: 1.5 * k, z: 1.5 * k, duration: 0.06 })
      .to(a.obj.scale, { y: a.base, x: a.base, z: a.base, duration: 0.22, ease: 'back.out(3)' });
    return new Promise((resolve) =>
      tl.eventCallback('onComplete', () => {
        a.busy = false;
        this.setGround(a, !isPewSpot(to));
        resolve();
      }),
    );
  }

  /** Where the Stamp of Precedence stands: at the outer end of its holder's pew, mirroring their standard. */
  private stampSpot(seat: number): THREE.Vector3 {
    return new THREE.Vector3((LOBBY_X[seat] ?? 0) + benchSide(seat) * BENCH_FLANK, 0.02, LOBBY_Z);
  }

  /** The Stamp of Precedence changes hands: it lifts off one bench and thumps down at another. */
  moveStamp(to: number, speed = 1): Promise<void> {
    const s = this.stamp;
    this.stampBusy = true;
    s.visible = true;
    const from = s.position.clone();
    const target = this.stampSpot(to);
    const p = { t: 0 };
    const tl = gsap.timeline();
    tl.timeScale(this.reducedMotion ? speed * 2 : speed);
    tl.to(s.position, { y: 1.2, duration: 0.35, ease: 'power2.out' })
      .to(s.rotation, { y: Math.PI * 2, duration: 1.1, ease: 'power1.inOut' }, 0)
      .to(p, {
        t: 1,
        duration: 0.9,
        ease: 'power1.inOut',
        onUpdate: () => {
          s.position.x = THREE.MathUtils.lerp(from.x, target.x, p.t);
          s.position.z = THREE.MathUtils.lerp(from.z, target.z, p.t);
          s.position.y = 1.2 + (this.reducedMotion ? 0 : 2.5 * 4 * p.t * (1 - p.t));
        },
      }, 0.2)
      .to(s.position, { y: target.y, duration: 0.18, ease: 'power3.in' })
      .to(s.scale, { y: 0.8 * STAMP_SCALE, x: 1.25 * STAMP_SCALE, z: 1.25 * STAMP_SCALE, duration: 0.07 })
      .to(s.scale, { y: STAMP_SCALE, x: STAMP_SCALE, z: STAMP_SCALE, duration: 0.3, ease: 'back.out(3)' });
    return new Promise((resolve) =>
      tl.eventCallback('onComplete', () => {
        s.rotation.y = 0;
        this.stampBusy = false;
        resolve();
      }),
    );
  }

  stationPosition(view: PlayerView, seat: number, id: string): THREE.Vector3 {
    return this.targetFor(view, seat, id);
  }

  assistantPosition(id: string): THREE.Vector3 | null {
    return this.assistants.get(id)?.obj.position.clone() ?? null;
  }

  isBusy(id: string): boolean {
    return this.assistants.get(id)?.busy ?? false;
  }

  /** Clock-out: every placed assistant hops home, staggered by seat. */
  async retrieveAll(view: PlayerView, speed = 1): Promise<void> {
    const jobs: Promise<void>[] = [];
    for (const st of view.seats) {
      const waiting = st.assistants.filter((a) => !a.temp);
      waiting.forEach((a, slot) => {
        const av = this.assistants.get(a.id);
        if (!av) return;
        const [bx, bz] = benchSlot(st.seat, slot, waiting.length);
        const to = new THREE.Vector3(bx, PEW_TOP, bz);
        if (av.obj.position.distanceTo(to) < 0.1) return;
        av.obj.visible = true;
        jobs.push(new Promise((r) => setTimeout(r, st.seat * 60)).then(() => this.hop(a.id, st.seat, to, speed * 1.4)));
      });
    }
    await Promise.all(jobs);
  }

  // ─── Rooms: highlights, blocks, bursts ─────────────────────────────────────

  setHighlights(map: Partial<Record<LocationId, Highlight>>): void {
    let changed = false;
    for (const [id, room] of this.rooms) {
      const h = map[id] ?? null;
      if (room.highlight !== h) changed = true;
      room.highlight = h;
    }
    if (!changed) return;
    for (const [id, room] of this.rooms) this.applyGlow(room, id === this.hovered);
  }

  private applyGlow(room: RoomView, hovered: boolean): void {
    room.pulse?.kill();
    room.pulse = null;
    const colors: Record<Exclude<Highlight, null>, string> = {
      legal: '#ffd86b',
      target: '#6fe3ff',
      audit: '#ff5a4f',
      dim: '#000000',
    };
    const h = room.highlight;
    // Rooms that are not an option while the player is choosing are dimmed so the options pop.
    const anyActive = [...this.rooms.values()].some((r) => r.highlight && r.highlight !== 'dim');
    const dimmed = anyActive && (!h || h === 'dim');
    room.floorMat.color.copy(room.baseFloor).multiplyScalar(dimmed ? 0.5 : 1);
    if (!h || h === 'dim') {
      room.rimMat.opacity = hovered ? 0.3 : 0;
      room.rimMat.color.set('#fff3d0');
      room.glowMat.opacity = 0;
    } else {
      room.rimMat.color.set(colors[h]);
      room.glowMat.color.set(colors[h]);
      if (hovered) {
        room.rimMat.opacity = 1;
        room.glowMat.opacity = 0.1;
      } else if (this.reducedMotion) {
        room.rimMat.opacity = 0.75;
        room.glowMat.opacity = 0;
      } else {
        room.rimMat.opacity = 0.45;
        room.glowMat.opacity = 0;
        room.pulse = gsap.to(room.rimMat, { opacity: 0.85, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut', data: AMBIENT });
      }
    }
    // Fully transparent overlays are hidden, not drawn: otherwise every room's rim and glow cost a
    // draw call each frame while unlit.
    room.rim.visible = room.rimMat.opacity > 0;
    room.glow.visible = room.glowMat.opacity > 0;
  }

  private setBlocked(_id: LocationId, blocked: boolean, room: RoomView): void {
    room.tape.visible = blocked;
  }

  /** An expanding ring on the room floor in a colour (resolution feedback). */
  burst(location: LocationId, color: string, speed = 1): Promise<void> {
    const r = this.layoutOf(location);
    if (!r) return Promise.resolve();
    const [cx, cz] = [(r.x[0] + r.x[1]) / 2, (r.z[0] + r.z[1]) / 2];
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.6, 0.9, 48), mat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(cx, FLOOR_Y + 0.05, cz);
    this.scene.add(ring);
    const room = this.rooms.get(location);
    if (room?.light) gsap.fromTo(room.light, { intensity: 90 }, { intensity: 30, duration: 0.6 / speed });
    return new Promise((resolve) => {
      gsap
        .timeline({ onComplete: () => (this.scene.remove(ring), ring.geometry.dispose(), mat.dispose(), resolve()) })
        .timeScale(speed)
        .to(ring.scale, { x: 5, y: 5, z: 5, duration: 0.6, ease: 'expo.out' }, 0)
        .to(mat, { opacity: 0, duration: 0.6, ease: 'power2.in' }, 0);
    });
  }

  /** Workday lighting: 9 AM cool → 5 PM golden; after hours for scoring (docs/04 §8). */
  setRound(round: number, ended = false): void {
    const t = THREE.MathUtils.clamp((round - 1) / 7, 0, 1);
    const sky = ended ? new THREE.Color('#8fa3d8') : new THREE.Color('#e6eeff').lerp(new THREE.Color('#ffc97a'), t);
    const sun = ended ? new THREE.Color('#7f90c8') : new THREE.Color('#fff4e0').lerp(new THREE.Color('#ffb25c'), t);
    const dur = this.reducedMotion ? 0 : 1.5;
    gsap.to(this.hemi.color, { r: sky.r, g: sky.g, b: sky.b, duration: dur });
    gsap.to(this.sun.color, { r: sun.r, g: sun.g, b: sun.b, duration: dur });
    gsap.to(this.sun, { intensity: ended ? 1.0 : 2.2 - t * 0.4, duration: dur });
    gsap.to(this.sun.position, { x: -18 + t * 36, duration: dur, onUpdate: () => void (this.renderer.shadowMap.needsUpdate = true), onComplete: () => void (this.renderer.shadowMap.needsUpdate = true) });
  }

  /** Gently bring an off-screen room into frame (Follow Action "subtle", docs/05 §7.3). */
  follow(location: LocationId): void {
    if (performance.now() - this.lastUserInput < 2000) return;
    const p = this.screenPos(this.roomAnchor(location));
    const rect = this.renderer.domElement.getBoundingClientRect();
    const margin = 60;
    if (p.x > rect.left + margin && p.x < rect.right - margin && p.y > rect.top + margin && p.y < rect.bottom - margin) return;
    const target = new THREE.Vector3();
    this.controls.getTarget(target);
    const lay = this.layoutOf(location);
    if (!lay) return;
    const [cx, cz] = [(lay.x[0] + lay.x[1]) / 2, (lay.z[0] + lay.z[1]) / 2];
    void this.controls.moveTo(THREE.MathUtils.lerp(target.x, cx, 0.6), 0, THREE.MathUtils.lerp(target.z, cz, 0.6), true);
  }

  // ─── Screen-space anchors ──────────────────────────────────────────────────

  /** Where a room's screen-space label sits: over the back third of the room, at head height. */
  labelAnchor(location: LocationId): THREE.Vector3 {
    const r = this.layoutOf(location) ?? ROOMS[0]!;
    const d = r.z[1] - r.z[0];
    return new THREE.Vector3((r.x[0] + r.x[1]) / 2, 1.4, r.z[0] + Math.min(1.9, d * 0.32));
  }

  /** Current highlight of a room (for the labels). */
  highlightOf(location: LocationId): Highlight {
    return this.rooms.get(location)?.highlight ?? null;
  }

  /** Hover a room from outside the canvas (its screen-space label). */
  hoverRoom(location: LocationId | null): void {
    if (!location) return this.setHover(null, 0, 0);
    const p = this.screenPos(this.labelAnchor(location));
    this.setHover(location, p.x, p.y + 14);
  }

  isHovered(location: LocationId): boolean {
    return this.hovered === location;
  }

  /** Ease the camera onto the spell display or the City Problems so their cards are readable. */
  focusCards(area: 'spells' | 'problems' | null): void {
    if (area === this.focused) return;
    const was = this.focused;
    this.focused = area;
    if (!area) {
      // Always come back from a market close-up: to the default view, or to the player's own
      // framing if they had set one before it. (Clicking a card to buy it counts as camera input,
      // so "has the player touched the camera" can't decide this on its own.)
      const pre = this.preFocus;
      this.preFocus = null;
      if (pre?.custom) void this.controls.setLookAt(pre.pos.x, pre.pos.y, pre.pos.z, pre.target.x, pre.target.y, pre.target.z, true);
      else this.resetCamera(true);
      return;
    }
    if (!was) {
      const pos = new THREE.Vector3();
      const target = new THREE.Vector3();
      this.controls.getPosition(pos);
      this.controls.getTarget(target);
      this.preFocus = { pos, target, custom: this.lastUserInput !== 0 };
    }
    const [spells, problems] = this.cards.footprints();
    const [x0, x1, z0, z1] = area === 'spells' ? spells : problems;
    // Keep clear of the HUD and of the decision bar above the tray (≈ 80 px).
    this.fitAreas([[x0, x1, z0, z1, 0.4]], THREE.MathUtils.degToRad(18), { ...this.safe, bottom: this.safe.bottom + 80 }, true);
  }
  private focused: 'spells' | 'problems' | null = null;
  private preFocus: { pos: THREE.Vector3; target: THREE.Vector3; custom: boolean } | null = null;

  /** Menu backdrop: no input, a slow drifting orbit (or a fixed pose, `still`, for capturing). */
  setAttract(on: boolean, centered = false, still = false): void {
    this.attract = on;
    this.attractStill = still;
    this.renderer.domElement.style.pointerEvents = on ? 'none' : '';
    this.cards.setVisible(!on);
    if (on) {
      // Board to the right of the menu column. The still (captured) pose pulls back a little so the
      // whole board survives cover-cropping and the CSS drift.
      const off = centered ? 0 : still ? -6 : -9;
      const k = still ? (centered ? 1.14 : 1.24) : 1;
      void this.controls.setLookAt(off, 34 * k, 40 * k, off, 0, -1, false);
      void this.controls.setFocalOffset(off, 0, 0, false);
    }
  }
  private attract = false;
  private attractStill = false;
  private attractT = 0;
  private keyArt = false;

  /**
   * Dev capture only (`npm run backdrop`): stage the title-screen key art. A free hero camera, dusk
   * light with the windows and lamps glowing, benches with waiting clerks and a few clerks caught
   * mid-hop on their way to work, with a trail of gilt dots behind them.
   */
  setKeyArt(variant: 'menu' | 'lobby'): void {
    this.keyArt = true;
    this.lastUserInput = performance.now(); // keep resize from re-framing
    this.renderer.domElement.style.pointerEvents = 'none';
    this.cards.setVisible(false);
    const c = this.controls;
    c.minAzimuthAngle = -Infinity;
    c.maxAzimuthAngle = Infinity;
    c.minPolarAngle = 0;
    c.maxPolarAngle = Math.PI / 2;
    c.minDistance = 1;
    c.maxDistance = 500;
    c.setBoundary();
    // Menu: a three-quarter view from the front left, the building right of the menu column with
    // room for the cards and the stamp in front. Lobby: centred, calmer.
    if (variant === 'menu') {
      void c.setLookAt(-40, 38, 64, 1, 0, 1, false);
      void c.setFocalOffset(-10, 4, 0, false);
    } else {
      void c.setFocalOffset(0, 0, 0, false);
      void c.setLookAt(0, 40, 57, 0, 0, 2.5, false);
    }

    // Dusk: a low warm sun, a cool dim sky, and every window, lamp and glowing centrepiece brighter.
    this.hemi.color.set('#b9b4ff');
    this.hemi.groundColor.set('#2a1a12');
    this.hemi.intensity = 0.34;
    this.sun.color.set('#ffb277');
    this.sun.intensity = 1.75;
    this.scene.environmentIntensity = 0.2;
    this.renderer.toneMappingExposure = 1.02;
    this.renderer.shadowMap.needsUpdate = true;
    this.scene.traverse((o) => {
      const mats = (o as THREE.Mesh).material;
      for (const m of Array.isArray(mats) ? mats : mats ? [mats] : []) {
        const sm = m as THREE.MeshStandardMaterial;
        if (sm.emissiveIntensity !== undefined && Math.abs(sm.emissiveIntensity - 0.12) < 0.001) sm.emissiveIntensity = 0.95; // stained glass
      }
      if (o.name === 'light-pools') ((o as THREE.Mesh).material as THREE.MeshBasicMaterial).color.setScalar(1.9);
      if ((o as THREE.PointLight).isPointLight) (o as THREE.PointLight).intensity *= 1.8;
    });

    // The lobby: four benches, each with a clerk waiting.
    const seats = 4;
    this.benches.forEach((b, i) => (b.visible = i < seats));
    const clerk = (seat: number, p: THREE.Vector3, tilt = 0) => {
      const av = this.makeAssistant(seat);
      av.obj.position.copy(p);
      av.obj.rotation.z = tilt;
      av.busy = true;
      // On the pew or in the air: no base ring or floor shadow, which would read as standing on the floor.
      this.setGround(av, false);
      return av;
    };
    // One clerk left on each pew, re-centred after its neighbour set off (the hop below starts from slot 1 of 2).
    for (let seat = 0; seat < seats; seat++) {
      const [bx, bz] = benchSlot(seat, 0, 1);
      clerk(seat, new THREE.Vector3(bx, PEW_TOP, bz));
    }
    // Clerks mid-hop: from their bench toward a room, frozen high on the arc.
    const dotMat = new THREE.MeshBasicMaterial({ color: '#ffd98a', transparent: true, opacity: 0.85, toneMapped: false });
    const dotGeo = new THREE.SphereGeometry(0.13, 10, 8);
    const hops: [number, LocationId, number][] = [
      [1, 'arcanium', 0.55],
      [2, 'grand-auditor', 0.48],
      [3, 'mana-conduit', 0.6],
      [0, 'precedence', 0.62],
    ];
    for (const [seat, loc, t] of hops) {
      const [bx, bz] = benchSlot(seat, 1, 2);
      const from = new THREE.Vector3(bx, PEW_TOP, bz);
      const r = ROOM_BY_ID.get(loc)!;
      const to = new THREE.Vector3(r.stations[0][0], FLOOR_Y + 0.08, r.stations[0][1]);
      const arc = 2 + 0.08 * from.distanceTo(to);
      const at = (u: number) => new THREE.Vector3().lerpVectors(from, to, u).setY(THREE.MathUtils.lerp(from.y, to.y, u) + arc * 4 * u * (1 - u));
      clerk(seat, at(t), (to.x > from.x ? -1 : 1) * 0.18);
      for (let u = 0.05; u < t - 0.06; u += 0.05) {
        const d = new THREE.Mesh(dotGeo, dotMat);
        d.position.copy(at(u));
        d.scale.setScalar(0.5 + u / t);
        this.scene.add(d);
      }
    }
  }

  roomAnchor(location: LocationId): THREE.Vector3 {
    const r = this.layoutOf(location);
    if (!r) return new THREE.Vector3(0, 1.6, 0);
    return new THREE.Vector3((r.x[0] + r.x[1]) / 2, 1.6, (r.z[0] + r.z[1]) / 2);
  }

  /** A room's layout: a fixed Ministry room, or an Annex room while it stands in a bay. */
  layoutOf(location: LocationId): RoomLayout | undefined {
    return this.layouts.get(location);
  }

  /** Is this room on the board and finished (an Annex room is only once its construction is done)? */
  roomReady(location: LocationId): boolean {
    if (!this.layouts.has(location)) return false;
    const bay = this.bays.find((b) => b.room?.location === location);
    return bay ? bay.room!.ready : true;
  }

  // ─── Annex bays (P-18) ─────────────────────────────────────────────────────

  /** Match the Annex bays to the view: shown with the rules option; each bay's room built at once. */
  private syncAnnex(view: PlayerView): void {
    const on = !!view.options.annexRooms;
    if (on !== this.annexOn) {
      this.annexOn = on;
      for (const b of this.bays) {
        b.group.visible = on;
        // A Classic game after one with the Annex (the scene is reused): nothing may stay pickable.
        if (!on && b.room) {
          this.removeAnnexRoom(b);
          b.showLot(true, false);
        }
      }
      // The wings reach past the sun's shadow box: widen it while they are there.
      const sc = this.sun.shadow.camera;
      sc.left = on ? -42 : -34;
      sc.right = on ? 42 : 34;
      sc.updateProjectionMatrix();
      this.renderer.shadowMap.needsUpdate = true;
      if (this.lastUserInput === 0) this.resetCamera(false);
    }
    if (!on) return;
    const bays = view.annex?.bays ?? [];
    this.bays.forEach((bay, i) => {
      if (bay.busy) return;
      const want = bays[i] ?? null;
      const have = bay.room?.location ?? null;
      if (want === have) return;
      if (bay.room) this.removeAnnexRoom(bay);
      if (want) this.addAnnexRoom(bay, want).showInstant();
      bay.showLot(!want, false);
      this.renderer.shadowMap.needsUpdate = true;
    });
  }

  private addAnnexRoom(bay: AnnexBay, location: LocationId): AnnexRoom {
    const room = new AnnexRoom(location, bay, props());
    bay.room = room;
    this.layouts.set(location, room.layout);
    room.group.updateMatrixWorld(true);
    this.buildRoom(room.layout, room);
    return room;
  }

  private removeAnnexRoom(bay: AnnexBay): void {
    const room = bay.room;
    if (!room) return;
    bay.room = null;
    const rv = this.rooms.get(room.location);
    rv?.pulse?.kill();
    this.rooms.delete(room.location);
    this.layouts.delete(room.location);
    if (this.hovered === room.location) this.hovered = null;
    room.dispose();
  }

  /**
   * AnnexOpened (docs/09 §7.1): a room opens in a bay. A room it replaces sends its clerks home,
   * has its sign carried off and comes down; then the new one is built. Resolves when it stands.
   */
  async openAnnex(
    view: PlayerView,
    location: LocationId,
    bayIndex: number,
    speed: number,
    cue: (c: 'land' | 'wall' | 'prop' | 'sign' | 'lights' | 'carry') => void,
  ): Promise<void> {
    const bay = this.bays[bayIndex];
    if (!bay || !this.annexOn) return;
    bay.busy = true;
    this.shadowLive++;
    try {
      const old = bay.room;
      if (old && old.location !== location) {
        await this.clearRoom(view, old.location, speed);
        await old.demolish(this.scene, speed, this.reducedMotion, (c) => cue(c === 'sign' ? 'carry' : c));
        this.removeAnnexRoom(bay);
        bay.showLot(true, true, speed);
        await wait(0.3 / speed);
      }
      if (bay.room?.location === location) return;
      const room = this.addAnnexRoom(bay, location);
      bay.showLot(false, true, speed);
      await room.build(this.scene, speed, this.reducedMotion, cue);
    } finally {
      bay.busy = false;
      this.shadowLive--;
      this.renderer.shadowMap.needsUpdate = true;
    }
  }

  /** Clerks in a room that is closing hop back to their pews. */
  private async clearRoom(view: PlayerView, location: LocationId, speed: number): Promise<void> {
    const jobs: Promise<void>[] = [];
    for (const st of view.seats) {
      const home = st.assistants.filter((a) => !a.temp && (a.location === location || (!a.location && !a.placed)));
      home.forEach((a, slot) => {
        if (a.location !== location) return;
        const [bx, bz] = benchSlot(st.seat, slot, home.length);
        jobs.push(this.hop(a.id, st.seat, new THREE.Vector3(bx, PEW_TOP, bz), speed * 1.3));
      });
    }
    await Promise.all(jobs);
  }

  /** Where the Annex bay's room stands (for the camera and the Director's effects). */
  bayAnchor(bayIndex: number): THREE.Vector3 {
    const b = ANNEX_BAYS[bayIndex]!;
    return new THREE.Vector3((b.x[0] + b.x[1]) / 2, 1.2, (b.z[0] + b.z[1]) / 2);
  }

  /**
   * A short, gentle move toward something happening off to the side (an Annex room going up), and
   * back to the table afterwards — only while the player hasn't taken the camera.
   */
  async lookAt(point: THREE.Vector3, hold: () => Promise<void>): Promise<void> {
    if (this.lastUserInput !== 0 || this.reducedMotion || this.focused) return hold();
    const target = new THREE.Vector3();
    const pos = new THREE.Vector3();
    this.controls.getTarget(target);
    this.controls.getPosition(pos);
    const t = target.clone().lerp(point.clone().setY(0), 0.32);
    const offset = pos.clone().sub(target).multiplyScalar(0.84);
    void this.controls.setLookAt(t.x + offset.x, t.y + offset.y, t.z + offset.z, t.x, t.y, t.z, true);
    try {
      await hold();
    } finally {
      if (this.lastUserInput === 0) this.resetCamera(!this.reducedMotion);
    }
  }

  // ─── Resolutions: the Ministry coin (Magical Weather) ──────────────────────

  private coinParts: { geo: THREE.CylinderGeometry; mats: THREE.Material[] } | null = null;

  /**
   * CoinFlipped (docs/09 §7.1, ~0.9 s): the Ministry's brass coin spins up over the Department and
   * comes down. Heads (the quill) flashes gold — the room's gains then repeat; tails (the scales) just
   * lands with a puff.
   */
  flipCoin(location: LocationId, result: 'heads' | 'tails', speed = 1): Promise<void> {
    const r = this.layoutOf(location);
    if (!r) return Promise.resolve();
    if (!this.coinParts) {
      const face = (icon: 'quill' | 'scales') => {
        const c = document.createElement('canvas');
        c.width = c.height = 256;
        const g = c.getContext('2d')!;
        const grad = g.createRadialGradient(100, 90, 10, 128, 128, 128);
        grad.addColorStop(0, '#fff0bf');
        grad.addColorStop(0.55, '#d8b05c');
        grad.addColorStop(1, '#8a6a2c');
        g.fillStyle = grad;
        g.fillRect(0, 0, 256, 256);
        g.strokeStyle = 'rgba(90,60,20,0.6)';
        g.lineWidth = 8;
        g.beginPath();
        g.arc(128, 128, 104, 0, Math.PI * 2);
        g.stroke();
        drawIcon(g, icon, 128, 128, 140, '#6b4a18');
        const t = new THREE.CanvasTexture(c);
        t.colorSpace = THREE.SRGBColorSpace;
        return new THREE.MeshStandardMaterial({ map: t, metalness: 0.75, roughness: 0.32 });
      };
      const edge = new THREE.MeshStandardMaterial({ color: '#b8893a', metalness: 0.9, roughness: 0.3 });
      this.coinParts = { geo: new THREE.CylinderGeometry(0.85, 0.85, 0.13, 40), mats: [edge, face('quill'), face('scales')] };
    }
    const coin = new THREE.Mesh(this.coinParts.geo, this.coinParts.mats);
    const cx = (r.x[0] + r.x[1]) / 2;
    const cz = (r.z[0] + r.z[1]) / 2;
    coin.position.set(cx, 2.2, cz);
    coin.scale.setScalar(0.001);
    coin.renderOrder = 6;
    this.scene.add(coin);
    const heads = result === 'heads';
    const turns = this.reducedMotion ? 1 : 4;
    const tl = gsap.timeline();
    tl.timeScale(speed);
    tl.to(coin.scale, { x: 1, y: 1, z: 1, duration: 0.12, ease: 'back.out(2)' }, 0)
      .to(coin.position, { y: 5.4, duration: 0.34, ease: 'power2.out' }, 0)
      .to(coin.rotation, { x: Math.PI * 2 * turns + (heads ? 0 : Math.PI), duration: 0.66, ease: 'power1.inOut' }, 0)
      .to(coin.position, { y: 1.35, duration: 0.32, ease: 'power2.in' }, 0.34)
      .call(() => {
        const at = new THREE.Vector3(cx, 1.4, cz);
        if (heads) {
          glint(this.scene, at.clone().add(new THREE.Vector3(0, 0.3, 0)), { size: 3.2, speed });
          void floorRing(this.scene, new THREE.Vector3(cx, FLOOR_Y + 0.06, cz), { color: '#ffd98a', radius: 3.4, speed, opacity: 0.42 });
          motes(this.scene, new THREE.Vector3(cx, 0.6, cz), { count: 8, spread: 1.2, rise: 2, speed, reduced: this.reducedMotion });
        } else {
          dust(this.scene, new THREE.Vector3(cx, 1.2, cz), { count: 4, size: 0.8, color: '#d8c8a8', speed, reduced: this.reducedMotion });
        }
      }, [], 0.66)
      .to(coin.position, { y: 1.55, duration: 0.1, ease: 'power1.out' }, 0.66)
      .to(coin.position, { y: 1.35, duration: 0.14, ease: 'bounce.out' }, 0.76)
      .to(coin.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.2, ease: 'back.in(2)' }, heads ? 1.15 : 1.0);
    return new Promise((resolve) =>
      tl.eventCallback('onComplete', () => {
        this.scene.remove(coin);
        resolve();
      }),
    );
  }

  benchAnchor(seat: number): THREE.Vector3 {
    return new THREE.Vector3(LOBBY_X[seat] ?? 0, 1, LOBBY_Z);
  }

  screenPos(v: THREE.Vector3): { x: number; y: number } {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const p = v.clone().project(this.camera);
    return { x: rect.left + ((p.x + 1) / 2) * rect.width, y: rect.top + ((1 - p.y) / 2) * rect.height };
  }

  // ─── Input ─────────────────────────────────────────────────────────────────

  private pick(clientX: number, clientY: number): LocationId | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const floors = [...this.rooms.values()].map((r) => r.floor);
    const hit = this.raycaster.intersectObjects(floors, false)[0];
    return (hit?.object.userData.location as LocationId | undefined) ?? null;
  }

  private pickCard(clientX: number, clientY: number): { pick: CardPick; group: THREE.Group | null } | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.intersectObjects(this.cards.pickables(), false)[0];
    if (!hit) return null;
    return { pick: hit.object.userData.pick as CardPick, group: this.cards.groupOf(hit.object) };
  }

  private setHover(loc: LocationId | null, x: number, y: number): void {
    if (loc !== this.hovered) {
      this.wake(); // also reached from the DOM room labels, which the canvas doesn't hear
      const prev = this.hovered ? this.rooms.get(this.hovered) : null;
      this.hovered = loc;
      if (prev) this.applyGlow(prev, false);
      const next = loc ? this.rooms.get(loc) : null;
      if (next) this.applyGlow(next, true);
      this.renderer.domElement.style.cursor = loc ? 'pointer' : 'default';
    }
    this.onRoomHover?.(loc, x, y);
  }

  private onPointerMove = (e: PointerEvent) => {
    if (e.pointerType === 'touch') {
      // Touch has no hover: a finger that starts dragging the camera drops the tap selection.
      if (this.down && Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y) > 10) this.clearTap();
      return;
    }
    const card = this.pickCard(e.clientX, e.clientY);
    if (card) {
      this.cards.hover(card.pick.kind === 'spell' || card.pick.kind === 'problem' || card.pick.kind === 'decree' ? card.group : null);
      this.setHover(null, e.clientX, e.clientY);
      this.renderer.domElement.style.cursor = 'pointer';
      this.onCardHover?.(card.pick, e.clientX, e.clientY);
      return;
    }
    this.cards.hover(null);
    this.onCardHover?.(null, e.clientX, e.clientY);
    this.setHover(this.pick(e.clientX, e.clientY), e.clientX, e.clientY);
  };
  private onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'touch') {
      this.touches.add(e.pointerId);
      // A second finger means a pinch or two-finger pan, never a tap.
      if (this.touches.size > 1) {
        this.down = null;
        this.clearTap();
        this.lastUserInput = performance.now();
        return;
      }
    }
    if (e.button === 0) this.down = { x: e.clientX, y: e.clientY };
    else this.lastUserInput = performance.now();
  };
  private onPointerCancel = (e: PointerEvent) => {
    this.touches.delete(e.pointerId);
    this.down = null;
  };
  private onPointerUp = (e: PointerEvent) => {
    const touch = e.pointerType === 'touch';
    if (touch) this.touches.delete(e.pointerId);
    if (e.button !== 0 || !this.down) return;
    const moved = Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y);
    this.down = null;
    if (moved > (touch ? 10 : 6)) return;
    const card = this.pickCard(e.clientX, e.clientY);
    const loc = card ? null : this.pick(e.clientX, e.clientY);
    if (touch) return this.tap(card, loc, e.clientX, e.clientY);
    if (card) return this.onCardClick?.(card.pick);
    if (loc) this.onRoomClick?.(loc);
  };

  /**
   * Touch: the first tap on a card or room inspects it (what hover shows with a mouse); a second tap
   * on the same thing acts on it; a tap anywhere else moves or clears the selection.
   */
  private tap(card: { pick: CardPick; group: THREE.Group | null } | null, loc: LocationId | null, x: number, y: number): void {
    const key = card ? `card:${JSON.stringify(card.pick)}` : loc ? `room:${loc}` : null;
    if (key && key === this.tapped) {
      this.clearTap();
      if (card) this.onCardClick?.(card.pick);
      else if (loc) this.onRoomClick?.(loc);
      return;
    }
    this.clearTap();
    this.tapped = key;
    if (card) {
      this.cards.hover(card.pick.kind === 'spell' || card.pick.kind === 'problem' || card.pick.kind === 'decree' ? card.group : null);
      this.onCardHover?.(card.pick, x, y);
    } else if (loc) this.setHover(loc, x, y);
  }

  /** Touch taps on a room's floating label follow the same inspect-then-act rule as the room itself. */
  tapRoom(loc: LocationId): void {
    if (this.tapped === `room:${loc}`) {
      this.clearTap();
      this.onRoomClick?.(loc);
      return;
    }
    this.clearTap();
    this.tapped = `room:${loc}`;
    this.hoverRoom(loc);
  }

  /** Drop the touch selection (and the inspect popup it opened). */
  clearTap(): void {
    if (this.tapped === null) return;
    this.tapped = null;
    this.cards.hover(null);
    this.onCardHover?.(null, 0, 0);
    this.setHover(null, 0, 0);
  }

  private onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement)?.closest?.('input, textarea')) return;
    const k = e.key.toLowerCase();
    if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'q', 'e'].includes(k)) {
      this.keys.add(k);
      this.lastUserInput = performance.now();
    }
    if (k === 'home') this.resetCamera();
  };
  private onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.key.toLowerCase());

  // ─── Loop ──────────────────────────────────────────────────────────────────

  private resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.post?.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // Dynamic resolution: if frames are consistently slow, render the scene at fewer pixels (the final
  // pass upsamples and sharpens); recover when there is headroom. The floor keeps the board crisp:
  // never below 1 scene pixel per CSS pixel on high-DPI screens, or 80% on standard ones. Judged
  // only on back-to-back full-rate frames (see loop), whose spacing is what a frame costs.
  private frameAvg = 16.7;
  private slowFor = 0;
  private fastFor = 0;
  private adaptResolution(rawDt: number): void {
    if (this.attract || this.keyArt || document.hidden || rawDt > 0.25) return;
    this.frameAvg = this.frameAvg * 0.95 + rawDt * 1000 * 0.05;
    if (!this.post) return;
    const scale = this.post.getScale();
    const floor = Math.max(0.5, Math.min(0.8, 1 / this.renderer.getPixelRatio()));
    if (this.frameAvg > 26) {
      this.slowFor += rawDt;
      this.fastFor = 0;
    } else if (this.frameAvg < 18) {
      this.fastFor += rawDt;
      this.slowFor = 0;
    } else {
      this.slowFor = this.fastFor = 0;
    }
    const set = (v: number) => {
      this.post!.setScale(v);
      this.slowFor = this.fastFor = 0;
      this.frameAvg = 16.7;
    };
    if (this.slowFor > 2 && scale > floor) set(Math.max(floor, +(scale - 0.1).toFixed(2)));
    else if (this.fastFor > 4 && scale < 1) set(Math.min(1, +(scale + 0.1).toFixed(2)));
  }

  // Frame pacing. The board is still most of the time — only the clerks' idle bob and slow pulses
  // move — so it is drawn only as often as what is happening needs (phones run hot drawing a still
  // board at up to 120 Hz):
  //   active   — camera moving, recent input, any tween that isn't an ambient loop: every vsync up
  //              to 90 Hz, every other one on faster screens (60 fps at 120 Hz);
  //   idle     — only ambient loops (pacing.ts): 30 fps at 60, 90 and 120 Hz;
  //   obscured — a modal over the board: 15 fps.
  private lastFrame = -Infinity;
  private activeUntil = 0;
  private wasActive = false;
  private frameListeners = new Set<() => void>();

  /** Render at the full rate for a moment (input, resize, a camera transition starting). */
  private wake = () => {
    this.activeUntil = Math.max(this.activeUntil, performance.now() + 500);
  };

  /** Run `cb` after every rendered frame (screen-space UI that follows the camera). */
  onFrame(cb: () => void): () => void {
    this.frameListeners.add(cb);
    return () => void this.frameListeners.delete(cb);
  }

  private isActive(now: number): boolean {
    if (now < this.activeUntil || this.keys.size > 0 || this.attract || this.keyArt) return true;
    for (const t of gsap.globalTimeline.getChildren(false, true, true)) if (t.data !== AMBIENT && t.isActive()) return true;
    return false;
  }

  private loop = (now: number) => {
    this.raf = requestAnimationFrame(this.loop);
    const since = now - this.lastFrame;
    // Minimum spacing per mode, with slack so frames land on whole vsyncs.
    if (since < 10) return;
    const obscured = boardObscured();
    const active = !obscured && this.isActive(now);
    if (!active && since < (obscured ? 60 : 26)) return;
    this.lastFrame = now;
    this.timer.update(now);
    const raw = this.timer.getDelta();
    if (active && this.wasActive) this.adaptResolution(raw);
    this.wasActive = active;
    const dt = Math.min(0.05, raw);
    const pan = 18 * dt;
    if (this.keys.has('w') || this.keys.has('arrowup')) void this.controls.forward(pan, false);
    if (this.keys.has('s') || this.keys.has('arrowdown')) void this.controls.forward(-pan, false);
    if (this.keys.has('a') || this.keys.has('arrowleft')) void this.controls.truck(-pan, 0, false);
    if (this.keys.has('d') || this.keys.has('arrowright')) void this.controls.truck(pan, 0, false);
    if (this.keys.has('q')) void this.controls.rotate(-0.8 * dt, 0, false);
    if (this.keys.has('e')) void this.controls.rotate(0.8 * dt, 0, false);
    if (this.attract) {
      if (!this.attractStill) this.attractT += dt;
      const az = Math.sin(this.attractT * 0.05) * 0.35;
      const polar = THREE.MathUtils.degToRad(40 + Math.sin(this.attractT * 0.07) * 5);
      void this.controls.rotateTo(az, polar, false);
    }
    // A camera still easing toward its target keeps the board at the full rate.
    if (this.controls.update(dt)) this.activeUntil = Math.max(this.activeUntil, now + 150);
    if (this.shadowLive > 0) this.renderer.shadowMap.needsUpdate = true;
    if (this.post) this.post.render(dt);
    else this.renderer.render(this.scene, this.camera);
    // Labels: a second, depth-cleared pass on their own layer, straight to the screen. World
    // matrices are already current from the main pass.
    const bg = this.scene.background;
    this.scene.background = null;
    this.scene.matrixWorldAutoUpdate = false;
    this.renderer.autoClear = false;
    this.renderer.clearDepth();
    this.camera.layers.set(LABEL_LAYER);
    this.renderer.render(this.scene, this.camera);
    this.camera.layers.set(0);
    this.renderer.autoClear = true;
    this.scene.matrixWorldAutoUpdate = true;
    this.scene.background = bg;
    for (const cb of this.frameListeners) cb();
  };

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.post?.dispose();
    this.envTarget.dispose();
    this.resizeObs.disconnect();
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    this.controls.dispose();
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose?.();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat?.dispose?.();
    });
    this.renderer.dispose();
    this.renderer.domElement.remove();
    disposeCardFaces();
    disposeTextures();
    disposeFx3d();
    this.decor.dispose();
  }
}

/** Position an object and return it (Object3D.position is read-only, so it can't be assigned). */
function at<T extends THREE.Object3D>(o: T, x: number, y: number, z: number): T {
  o.position.set(x, y, z);
  return o;
}

/** A player's standard beside their bench: a brass-capped pole flying a swallowtail pennant. */
/** Whether a resting position is on a lobby pew (rather than at a room station). */
function isPewSpot(p: THREE.Vector3): boolean {
  return Math.abs(p.y - PEW_TOP) < 0.01 && Math.abs(p.z - LOBBY_Z) < 0.01;
}

/** The seat's standard, at the pew's inner end; its flag flies toward the entrance. */
function seatStandard(color: string, side: number): THREE.Group {
  const g = new THREE.Group();
  const iron = toonFrom({ color: '#2e2a28', metalness: 0.8 });
  const brass = toonFrom({ color: '#c9a14e', metalness: 1 });
  const cloth = toonFrom({ color, side: THREE.DoubleSide });
  const pole = at(new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 2.9, 10), iron), 0, 1.45, 0);
  const foot = at(new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.28, 0.14, 12), iron), 0, 0.07, 0);
  const tip = at(new THREE.Mesh(new THREE.OctahedronGeometry(0.1, 0), brass), 0, 3.0, 0);
  const bar = at(new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 1.0, 8), brass), 0.5, 2.8, 0);
  bar.rotation.z = Math.PI / 2;
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(0.9, 0);
  shape.lineTo(0.9, -1.3);
  shape.lineTo(0.45, -1.0);
  shape.lineTo(0, -1.3);
  shape.closePath();
  const flag = at(new THREE.Mesh(new THREE.ShapeGeometry(shape), cloth), 0.05, 2.78, 0);
  flag.rotation.y = -0.35;
  for (const m of [pole, foot, tip, bar, flag]) m.castShadow = true;
  g.add(pole, foot, tip, bar, flag);
  g.position.set(-side * BENCH_FLANK, 0, 0);
  if (side > 0) g.rotation.y = Math.PI; // east benches: the flag flies west, toward the entrance
  return g;
}

/** A rug in the seat colour under the player's whole corner: standard, pew and the Stamp's spot. No
 * medallion: the pew would half-cover it. */
function seatRug(color: string): THREE.Mesh {
  const field = new THREE.Color(color).lerp(new THREE.Color('#2a1a12'), 0.45).getStyle();
  const [w, d] = [2 * BENCH_FLANK + 1.3, 2.2];
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), toonFrom({ map: carpet(field, '#c9a14e', w, d, `seat-${color}`, false) }));
  m.rotation.x = -Math.PI / 2;
  m.position.set(0, 0.03, 0.2);
  m.receiveShadow = true;
  return m;
}

/** The Stamp of Precedence: a gilt hand stamp with a dark wooden grip and a red rubber face. */
export function makeStamp(): THREE.Group {
  const g = new THREE.Group();
  const gold = new THREE.MeshStandardMaterial({ color: '#d8b05c', metalness: 0.9, roughness: 0.28 });
  const wood = new THREE.MeshStandardMaterial({ color: '#3a2216', roughness: 0.55 });
  const pad = new THREE.MeshStandardMaterial({ color: '#9e1f2c', roughness: 0.7 });
  const parts: [THREE.BufferGeometry, THREE.Material, number][] = [
    [new THREE.CylinderGeometry(0.34, 0.34, 0.06, 28), pad, 0.03],
    [new THREE.CylinderGeometry(0.36, 0.38, 0.14, 28), gold, 0.13],
    [new THREE.CylinderGeometry(0.09, 0.14, 0.3, 16), gold, 0.35],
    [new THREE.CylinderGeometry(0.15, 0.12, 0.42, 16), wood, 0.7],
    [new THREE.SphereGeometry(0.19, 20, 14), gold, 0.98],
  ];
  for (const [geo, mat, y] of parts) {
    const m = new THREE.Mesh(geo, mat);
    m.position.y = y;
    m.castShadow = true;
    g.add(m);
  }
  g.name = 'stamp-of-precedence';
  return g;
}

